import React, { createContext, useContext, useReducer, ReactNode, useEffect, useMemo, useRef } from 'react';
import { TIERS, Tier, getTierByPodCount, getNextTier, getProgressToNextTier } from '../../../config/tiers';
import { useNespressoTESSync } from '../../../hooks/useNespressoTESSync';

// Re-export TIERS as NESPRESSO_TIERS for backwards compatibility
export { TIERS as NESPRESSO_TIERS };
export type { Tier };

// Alias for GamificationTier for backwards compatibility
export type GamificationTier = Tier;

// Gamification state
export interface GamificationState {
  lifetimePods: number;
  demoMode: boolean;
  celebratingTierId: string | null;
  pendingRewards: string[];
  achievedTiers: string[];
}

// Combined state
interface NespressoState {
  gamification: GamificationState;
  userEmail: string | null;
}

// Action types
type NespressoAction =
  | { type: 'SET_LIFETIME_PODS'; payload: number }
  | { type: 'INCREMENT_LIFETIME_PODS'; payload: number }
  | { type: 'TOGGLE_DEMO_MODE' }
  | { type: 'TRIGGER_MILESTONE'; payload: string }
  | { type: 'DISMISS_CELEBRATION' }
  | { type: 'CLAIM_REWARD'; payload: string }
  | { type: 'RESET_GAMIFICATION' }
  | { type: 'SET_EMAIL'; payload: string }
  | { type: 'CLEAR_MILESTONE' }
  | { type: 'LOAD_FROM_TES'; payload: { lifetimePods: number; achievedTiers: string[] } };

// Initial state
const initialState: NespressoState = {
  gamification: {
    lifetimePods: 0,
    demoMode: false,
    celebratingTierId: null,
    pendingRewards: [],
    achievedTiers: ['first-sip'],
  },
  userEmail: null,
};

// Reducer
function nespressoReducer(state: NespressoState, action: NespressoAction): NespressoState {
  switch (action.type) {
    case 'SET_LIFETIME_PODS': {
      const newPods = Math.max(0, action.payload);
      const newTier = getTierByPodCount(newPods);
      const oldTier = getTierByPodCount(state.gamification.lifetimePods);

      // Check if we crossed into a new tier (upgrade)
      const didUpgrade = newTier.threshold > oldTier.threshold;

      // Track newly achieved tiers
      const newlyAchieved = TIERS.filter(
        tier => tier.threshold <= newPods && !state.gamification.achievedTiers.includes(tier.id)
      ).map(tier => tier.id);

      return {
        ...state,
        gamification: {
          ...state.gamification,
          lifetimePods: newPods,
          achievedTiers: [...state.gamification.achievedTiers, ...newlyAchieved],
          celebratingTierId: didUpgrade && newTier.id !== oldTier.id ? newTier.id : state.gamification.celebratingTierId,
        },
      };
    }

    case 'INCREMENT_LIFETIME_PODS': {
      const newPods = Math.max(0, state.gamification.lifetimePods + action.payload);
      const newTier = getTierByPodCount(newPods);
      const oldTier = getTierByPodCount(state.gamification.lifetimePods);

      // Check if we crossed into a new tier (upgrade)
      const didUpgrade = newTier.threshold > oldTier.threshold;

      // Track newly achieved tiers
      const newlyAchieved = TIERS.filter(
        tier => tier.threshold <= newPods && !state.gamification.achievedTiers.includes(tier.id)
      ).map(tier => tier.id);

      return {
        ...state,
        gamification: {
          ...state.gamification,
          lifetimePods: newPods,
          achievedTiers: [...state.gamification.achievedTiers, ...newlyAchieved],
          celebratingTierId: didUpgrade && newTier.id !== oldTier.id ? newTier.id : state.gamification.celebratingTierId,
        },
      };
    }

    case 'TOGGLE_DEMO_MODE':
      return {
        ...state,
        gamification: {
          ...state.gamification,
          demoMode: !state.gamification.demoMode,
        },
      };

    case 'TRIGGER_MILESTONE':
      return {
        ...state,
        gamification: {
          ...state.gamification,
          celebratingTierId: action.payload,
        },
      };

    case 'DISMISS_CELEBRATION':
    case 'CLEAR_MILESTONE':
      return {
        ...state,
        gamification: {
          ...state.gamification,
          celebratingTierId: null,
        },
      };

    case 'CLAIM_REWARD':
      return {
        ...state,
        gamification: {
          ...state.gamification,
          pendingRewards: state.gamification.pendingRewards.filter(id => id !== action.payload),
        },
      };

    case 'RESET_GAMIFICATION':
      return {
        ...state,
        gamification: {
          ...initialState.gamification,
          demoMode: state.gamification.demoMode, // Preserve demo mode
        },
        userEmail: null,
      };

    case 'SET_EMAIL':
      return {
        ...state,
        userEmail: action.payload,
      };

    case 'LOAD_FROM_TES':
      // Load state from TES without triggering celebration
      return {
        ...state,
        gamification: {
          ...state.gamification,
          lifetimePods: action.payload.lifetimePods,
          achievedTiers: action.payload.achievedTiers,
          // Don't trigger celebration when loading from TES
          celebratingTierId: null,
        },
      };

    default:
      return state;
  }
}

// Context type with computed values
interface NespressoContextType {
  state: NespressoState;
  dispatch: React.Dispatch<NespressoAction>;
  // Computed tier helpers
  currentTier: Tier;
  nextTier: Tier | undefined;
  progressToNext: number;
  podsToNext: number;
}

// Create context
const NespressoContext = createContext<NespressoContextType | undefined>(undefined);

// Storage key
const STORAGE_KEY = 'nespresso-gamification';

// Provider
interface NespressoProviderProps {
  children: ReactNode;
}

export const NespressoProvider: React.FC<NespressoProviderProps> = ({ children }) => {
  const hasLoadedFromTESRef = useRef(false);
  const previousTierRef = useRef<string | null>(null);

  // Load initial state from localStorage
  const getInitialState = (): NespressoState => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...initialState,
          gamification: {
            ...initialState.gamification,
            ...parsed.gamification,
            // Always clear celebrating tier on load
            celebratingTierId: null,
          },
          userEmail: parsed.userEmail || null,
        };
      }
    } catch (error) {
      console.error('Failed to load Nespresso state from localStorage:', error);
    }
    return initialState;
  };

  const [state, dispatch] = useReducer(nespressoReducer, undefined, getInitialState);

  // TES sync - use userEmail as userId (auth handled server-side)
  const { loadFromTES, saveToTES, emitMilestoneAchieved, isEnabled: tesSyncEnabled } = useNespressoTESSync({
    userId: state.userEmail || undefined,
  });

  // Load state from TES on mount
  useEffect(() => {
    if (tesSyncEnabled && !hasLoadedFromTESRef.current) {
      hasLoadedFromTESRef.current = true;

      loadFromTES().then((tesState) => {
        if (tesState) {
          // If TES has more pods, use TES state (cloud is source of truth)
          if (tesState.lifetimePods > state.gamification.lifetimePods) {
            console.log('[Nespresso] Loading state from TES:', tesState);
            dispatch({
              type: 'LOAD_FROM_TES',
              payload: tesState
            });
          } else if (state.gamification.lifetimePods > tesState.lifetimePods) {
            // If local has more, sync local to TES
            console.log('[Nespresso] Local state ahead, syncing to TES');
            saveToTES({
              lifetimePods: state.gamification.lifetimePods,
              achievedTiers: state.gamification.achievedTiers,
            });
          }
        }
      }).catch((err) => {
        console.error('[Nespresso] Failed to load from TES:', err);
      });
    }
  }, [tesSyncEnabled, loadFromTES, saveToTES, state.gamification.lifetimePods, state.gamification.achievedTiers]);

  // Sync state to TES when it changes (debounced via the hook)
  useEffect(() => {
    if (tesSyncEnabled && hasLoadedFromTESRef.current) {
      saveToTES({
        lifetimePods: state.gamification.lifetimePods,
        achievedTiers: state.gamification.achievedTiers,
      }).catch((err) => {
        console.error('[Nespresso] Failed to sync to TES:', err);
      });
    }
  }, [tesSyncEnabled, saveToTES, state.gamification.lifetimePods, state.gamification.achievedTiers]);

  // Persist state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        gamification: {
          lifetimePods: state.gamification.lifetimePods,
          demoMode: state.gamification.demoMode,
          achievedTiers: state.gamification.achievedTiers,
          pendingRewards: state.gamification.pendingRewards,
        },
        userEmail: state.userEmail,
      }));
    } catch (error) {
      console.error('Failed to save Nespresso state to localStorage:', error);
    }
  }, [state.gamification.lifetimePods, state.gamification.demoMode, state.gamification.achievedTiers, state.gamification.pendingRewards, state.userEmail]);

  // Computed values
  const currentTier = useMemo(() => getTierByPodCount(state.gamification.lifetimePods), [state.gamification.lifetimePods]);

  // Emit milestone event when tier changes
  useEffect(() => {
    if (!currentTier) return;

    // Initialize on first render
    if (previousTierRef.current === null) {
      previousTierRef.current = currentTier.id;
      return;
    }

    // Check if tier upgraded
    if (currentTier.id !== previousTierRef.current) {
      const previousTier = TIERS.find(t => t.id === previousTierRef.current);
      const isUpgrade = previousTier ? currentTier.threshold > previousTier.threshold : false;

      if (isUpgrade && tesSyncEnabled) {
        console.log('[Nespresso] Tier upgraded! Emitting milestone event:', currentTier.id);
        emitMilestoneAchieved(
          currentTier.id,
          currentTier.name,
          state.gamification.lifetimePods
        ).catch((err) => {
          console.error('[Nespresso] Failed to emit milestone:', err);
        });
      }

      previousTierRef.current = currentTier.id;
    }
  }, [currentTier, tesSyncEnabled, emitMilestoneAchieved, state.gamification.lifetimePods]);
  const nextTier = useMemo(() => getNextTier(currentTier), [currentTier]);
  const progress = useMemo(() => getProgressToNextTier(state.gamification.lifetimePods), [state.gamification.lifetimePods]);

  const value = useMemo(() => ({
    state,
    dispatch,
    currentTier,
    nextTier,
    progressToNext: progress.progressPercent,
    podsToNext: progress.podsRemaining,
  }), [state, currentTier, nextTier, progress]);

  return (
    <NespressoContext.Provider value={value}>
      {children}
    </NespressoContext.Provider>
  );
};

// Custom hook
export function useNespresso() {
  const context = useContext(NespressoContext);
  if (context === undefined) {
    throw new Error('useNespresso must be used within a NespressoProvider');
  }
  return context;
}

// Helper functions for external use
export { getTierByPodCount as getCurrentTier, getTierByPodCount as getTierByPods, getNextTier };

export default NespressoContext;
